// src/components/XPModal.tsx
import React, { useMemo } from 'react';
import { format, startOfDay, startOfWeek, startOfMonth } from 'date-fns';
import { LevelIcon } from './LevelIcon';
import { useDarkMode } from '../context/DarkModeContext';
import { useQuery } from 'convex/react';
import { api } from '@careercard/convex/convex/_generated/api';
import type { UserLevel } from '../types/xp';

interface XPModalProps {
  isOpen: boolean;
  onClose: () => void;
  xpGained?: number;
  level?: number;
  totalXP?: number;
  nextLevelXP?: number;
}

// Level name mapping based on numeric level (matches useConvexXP.ts)
function getLevelName(level: number): UserLevel {
  if (level === 0) return 'explorer';
  if (level === 1) return 'novice';
  if (level <= 3) return 'developer';
  if (level <= 5) return 'strategist';
  if (level <= 7) return 'master';
  if (level <= 9) return 'virtuoso';
  if (level <= 12) return 'sage';
  return 'legend';
}

// Helper to categorize action types for stats
function getActionCategory(actionType: string): 'note' | 'card' | 'review' | 'other' {
  const lowerType = actionType.toLowerCase();
  if (lowerType.includes('note')) return 'note';
  if (lowerType.includes('card') || lowerType.includes('career_card')) return 'card';
  if (lowerType.includes('review')) return 'review';
  return 'other';
}

export const XPModal: React.FC<XPModalProps> = ({
  isOpen,
  onClose,
  // These props are passed but we fetch fresh data instead

  xpGained: _xpGained,

  level: _level,

  totalXP: _totalXP,

  nextLevelXP: _nextLevelXP,
}) => {
  const { darkMode } = useDarkMode();

  // Use Convex queries for real-time data
  const xpData = useQuery(api.gamification.getXp);
  const streakData = useQuery(api.gamification.getStreak);
  // Get more history to cover the full month for stats calculation
  const xpHistoryData = useQuery(api.gamification.getXpHistory, { limit: 100 });

  // Calculate daily/weekly/monthly stats from XP history
  const stats = useMemo(() => {
    if (!xpHistoryData?.events) {
      return {
        daily_xp: { note: 0, career_card: 0, tag: 0 },
        weekly_actions: { notes: 0, career_cards: 0, tags: 0 },
        monthly_actions: { notes_created: 0, cards_created: 0, reviews: 0 },
      };
    }

    const now = new Date();
    const todayStart = startOfDay(now).getTime();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 }).getTime(); // Monday
    const monthStart = startOfMonth(now).getTime();

    const daily_xp: Record<string, number> = { note: 0, career_card: 0, tag: 0 };
    const weekly_actions: Record<string, number> = { notes: 0, career_cards: 0, tags: 0 };
    const monthly_actions: Record<string, number> = { notes_created: 0, cards_created: 0, reviews: 0 };

    for (const event of xpHistoryData.events) {
      const eventTime = event.createdAt;
      const category = getActionCategory(event.actionType);
      const isCreate = event.actionType.toLowerCase().includes('create');

      // Daily XP totals (by XP amount)
      if (eventTime >= todayStart) {
        if (category === 'note') daily_xp.note += event.xpEarned;
        else if (category === 'card') daily_xp.career_card += event.xpEarned;
        else daily_xp.tag += event.xpEarned; // Group other as "tag" for backwards compat
      }

      // Weekly action counts
      if (eventTime >= weekStart) {
        if (category === 'note') weekly_actions.notes += 1;
        else if (category === 'card') weekly_actions.career_cards += 1;
        else weekly_actions.tags += 1;
      }

      // Monthly action counts (create actions only)
      if (eventTime >= monthStart && isCreate) {
        if (category === 'note') monthly_actions.notes_created += 1;
        else if (category === 'card') monthly_actions.cards_created += 1;
        else if (category === 'review') monthly_actions.reviews += 1;
      }
    }

    return { daily_xp, weekly_actions, monthly_actions };
  }, [xpHistoryData]);

  // Transform Convex data to match the expected format
  const progress = useMemo(() => {
    if (!xpData || !streakData) return null;

    return {
      xp: {
        current_level: getLevelName(xpData.currentLevel),
        total_xp: xpData.totalXp,
        daily_xp: stats.daily_xp,
        weekly_actions: stats.weekly_actions,
        monthly_actions: stats.monthly_actions,
      },
      streak: {
        current_streak: streakData.currentStreak,
        longest_streak: streakData.longestStreak,
      },
    };
  }, [xpData, streakData, stats]);

  // Transform XP history to match expected format
  const history = useMemo(() => {
    if (!xpHistoryData) return [];

    return xpHistoryData.events.map((event: { _id: string; actionType: string; xpEarned: number; metadata?: Record<string, unknown>; createdAt: number }) => ({
      id: event._id,
      action_type: event.actionType,
      xp_earned: event.xpEarned,
      metadata: event.metadata || {},
      created_at: new Date(event.createdAt).toISOString(),
    }));
  }, [xpHistoryData]);

  const formatActionType = (actionType: string, metadata: Record<string, unknown>) => {
    switch (actionType) {
      case 'note':
        return metadata.action === 'create' ? 'Created a new note' : 'Updated a note';
      case 'card':
        return metadata.action === 'create' ? 'Created a career card' : 'Updated a career card';
      case 'review':
        return 'Completed a review';
      case 'streak':
        return 'Daily streak bonus';
      default:
        return actionType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
  };

  const getActionDetails = (_metadata: Record<string, unknown>) => {
    return '';
  };

  const renderStats = () => {
    if (!progress) return null;

    const { daily_xp, weekly_actions, monthly_actions } = progress.xp;

    return (
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className={`${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900'} p-4 rounded`}>
          <h4 className="text-lg font-medium mb-2">Today's XP</h4>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Notes</span>
              <span>{daily_xp.note || 0} XP</span>
            </div>
            <div className="flex justify-between">
              <span>Cards</span>
              <span>{daily_xp.career_card || 0} XP</span>
            </div>
            <div className="flex justify-between">
              <span>Tags</span>
              <span>{daily_xp.tag || 0} XP</span>
            </div>
          </div>
        </div>

        <div className={`${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900'} p-4 rounded`}>
          <h4 className="text-lg font-medium mb-2">This Week</h4>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Notes</span>
              <span>{weekly_actions.notes || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Cards</span>
              <span>{weekly_actions.career_cards || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Tags</span>
              <span>{weekly_actions.tags || 0}</span>
            </div>
          </div>
        </div>

        <div className={`${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900'} p-4 rounded`}>
          <h4 className="text-lg font-medium mb-2">This Month</h4>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Notes Created</span>
              <span>{monthly_actions.notes_created || monthly_actions.reflections || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Cards Created</span>
              <span>{monthly_actions.cards_created || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Reviews</span>
              <span>{monthly_actions.reviews || 0}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Don't render if modal is not open
  if (!isOpen) return null;

  // Show loading state if data is still loading
  if (!progress) {
    return (
      <>
        <div className="fixed inset-0 z-50" onClick={onClose} />
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} rounded-lg p-6 max-w-2xl w-full shadow-2xl pointer-events-auto`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">XP Progress</h2>
              <button
                type="button"
                onClick={onClose}
                className={`${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-500 hover:text-gray-700'}`}
              >
                ✕
              </button>
            </div>
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3">Loading XP data...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Transparent overlay for click-away functionality */}
      <div className="fixed inset-0 z-50" onClick={onClose} />
      
      {/* Modal content */}
      <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
        <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl pointer-events-auto`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">XP Progress</h2>
          <button
            type="button"
            onClick={onClose}
            className={`${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-500 hover:text-gray-700'}`}
          >
            ✕
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className={`${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900'} p-4 rounded`}>
            <div className="text-lg font-medium">Level</div>
            <div className="flex items-center gap-2">
              <LevelIcon level={progress.xp.current_level} />
              <span className="capitalize">{progress.xp.current_level}</span>
            </div>
            <div className="text-sm mt-2">Total XP: {progress.xp.total_xp}</div>
          </div>
          
          <div className={`${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900'} p-4 rounded`}>
            <div className="text-lg font-medium">Streak</div>
            <div className="flex flex-col">
              <div>Current: {progress.streak.current_streak} days</div>
              <div className="text-sm">Longest: {progress.streak.longest_streak} days</div>
            </div>
          </div>
        </div>

        {renderStats()}

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-4">Recent Activity</h3>
          <div className="space-y-2">
            {history.map((entry: { id: string; action_type: string; xp_earned: number; metadata: Record<string, unknown>; created_at: string }) => (
              <div key={entry.id} className={`flex justify-between items-center p-4 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900'} rounded`}>
                <div className="grow">
                  <div className="font-medium">
                    {formatActionType(entry.action_type, entry.metadata)}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {format(new Date(entry.created_at), 'MMM d, yyyy h:mm a')}
                    </div>
                    {getActionDetails(entry.metadata) && (
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {getActionDetails(entry.metadata)}
                      </div>
                    )}
                  </div>
                </div>
                <div className={`flex items-center gap-2 ${darkMode ? 'text-green-400' : 'text-green-600'} font-medium ml-4`}>
                  <span className="text-lg">+{entry.xp_earned}</span>
                  <span className="text-sm">XP</span>
                </div>
              </div>
            ))}
          </div>
        </div>

{/* Rewards section - could be added back when Convex gamification supports rewards */}

        <div className="mt-4 text-center border-t pt-4">
          <a
            href="/gamification-support"
            className="text-blue-600 hover:text-blue-800 text-sm"
            onClick={onClose}
          >
            Learn more about XP and Leveling
          </a>
        </div>
      </div>
      </div>
    </>
  );
};